import { Search } from "lucide-react";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { PageHeader } from "@/components/data-display/page-header";
import { UnavailableValue } from "@/components/data-display/unavailable-value";
import { FilterBar } from "@/components/data-display/filter-bar";
import { Select } from "@/components/ui/select";
import { Table, TableWrapper } from "@/components/ui/table";
import { EmptyRow, Panel } from "@/features/oversight/oversight-ui";
import { WIP_ACTION_HINT, WipHint } from "@/components/ui/wip-hint";

/**
 * WIP layout port (round 3, ALL-5825) of the designer's `/governance/evaluation`
 * screen — `.design-ref/prototype/app/(enterprise-wallet)/governance/evaluation/page.tsx`
 * → `EvaluationHistory` in `features/oversight/governance.tsx`.
 *
 * The layout is the designer's (breadcrumb, header, the four statistic tiles,
 * the filter bar, the 1fr + 340px evaluation table and receipt panel); the data
 * is not: policy evaluations have no backend route in this environment yet
 * (context/page_pipeline/BACKEND_GAPS.md), so every value renders the shared
 * "not available" placeholder, the table its empty state, and the filters —
 * which have nothing to filter — stay disabled with `WIP_ACTION_HINT`.
 *
 * No endpoint is called and no evaluation, count or receipt is fabricated.
 */

/** Statistic tiles the designer draws. Labels are the design's; values are not known here. */
const EVAL_STAT_LABELS = ["Evaluations Today", "Passed", "Held / Requires Approval", "Blocked"];

/** Receipt fields the designer lists for a selected evaluation. */
const RECEIPT_FIELDS = ["Evaluation", "Policy", "Intent", "Result", "Actor", "Timestamp"];

const WIP_FILTER_DESC_ID = "governance-evaluation-wip-filters";

/**
 * A stat tile whose value the backend does not carry. `StatCard` takes a string
 * value and would print its own dash, but the honest value here is the shared
 * placeholder component, so this keeps `StatCard`'s shell classes and swaps only
 * the value line.
 */
function UnavailableStat({ label }: { label: string }) {
  return (
    <div className="rounded-lg border border-line bg-surface px-5 py-[18px]">
      <div className="text-[15px] font-bold leading-[1.2] tracking-tight">
        <UnavailableValue />
      </div>
      <div className="mt-2 text-xs text-muted">{label}</div>
    </div>
  );
}

export default function GovernanceEvaluationPage() {
  return (
    <div>
      <Breadcrumb items={[{ label: "Governance", href: "/governance" }, { label: "Evaluation History" }]} />

      <PageHeader
        title="Policy Evaluation History"
        subtitle="Every policy decision, newest first, with its evidence receipt."
        actions={<WipHint />}
      />

      <p className="mb-5 text-xs text-muted">
        Policy evaluations have no backend route in this environment yet — the tiles, filters and table below are the
        designed layout with no evaluation data.
      </p>

      {/* One column on a phone, two from sm, four from lg: the value slot holds
          the words "Not available yet", which does not fit a half-width 375px
          tile without pushing the page wider than the viewport. */}
      <div
        data-testid="governance-evaluation-stats"
        className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
      >
        {EVAL_STAT_LABELS.map((label) => (
          <UnavailableStat key={label} label={label} />
        ))}
      </div>

      <FilterBar className="mb-5">
        <div className="relative min-w-[200px] max-w-[300px] flex-1">
          <Search aria-hidden className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted" />
          <input
            type="text"
            disabled
            placeholder="Search evaluations by ID, policy, intent…"
            aria-label="Search evaluations"
            title={WIP_ACTION_HINT}
            aria-describedby={WIP_FILTER_DESC_ID}
            className="w-full py-[7px] pl-9 pr-2 text-sm text-ink outline-none placeholder:text-subtle disabled:cursor-not-allowed disabled:text-[var(--ads-text-disabled)]"
          />
        </div>
        <Select
          disabled
          aria-label="Filter by result"
          title={WIP_ACTION_HINT}
          aria-describedby={WIP_FILTER_DESC_ID}
          defaultValue=""
        >
          <option value="">All Results</option>
          <option value="Passed">Passed</option>
          <option value="Blocked">Blocked</option>
          <option value="Held">Held</option>
          <option value="Requires Approval">Requires Approval</option>
        </Select>
        <span id={WIP_FILTER_DESC_ID} className="sr-only">
          {WIP_ACTION_HINT}
        </span>
      </FilterBar>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
        <TableWrapper>
          <Table className="dense">
            <thead>
              <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
                <th className="px-4 py-2.5">Eval ID</th>
                <th className="px-4 py-2.5">Policy</th>
                <th className="px-4 py-2.5">Intent</th>
                <th className="px-4 py-2.5">Result</th>
                <th className="px-4 py-2.5">Actor</th>
                <th className="px-4 py-2.5">Timestamp</th>
              </tr>
            </thead>
            <tbody>
              <EmptyRow colSpan={6}>No evaluations to show.</EmptyRow>
            </tbody>
          </Table>
        </TableWrapper>

        <Panel title="Receipt" bodyClassName="px-5 py-3">
          <dl className="divide-y divide-line">
            {RECEIPT_FIELDS.map((label) => (
              <div key={label} className="flex items-start justify-between gap-3 py-2.5 text-xs">
                <dt className="shrink-0 text-muted">{label}</dt>
                <dd className="min-w-0 break-words text-right font-semibold text-ink">
                  <UnavailableValue />
                </dd>
              </div>
            ))}
            <div className="flex items-center justify-between gap-3 py-2.5 text-xs">
              <dt className="shrink-0 text-muted">Evidence</dt>
              <dd className="min-w-0 text-right">
                <UnavailableValue />
              </dd>
            </div>
          </dl>
        </Panel>
      </div>
    </div>
  );
}
